/**
 * Site-wide deferred JS (loaded with `defer`, run at browser idle time).
 * Blade-resolved URLs arrive via `window.__siteJsConfig`, emitted by an
 * inline script in master.blade.php before this file loads.
 */
(function () {
    'use strict';

    // ===========================================================================
    // Config from Blade-rendered window globals (see master.blade.php inline init)
    // ===========================================================================
    var config = (window.__siteJsConfig) || {};
    var BASE_URL = config.baseUrl || '';
    var IMG_MISSING = config.imgMissing || '';
    var IMG_LOADING = config.imgLoading || '';

    // .clckcatalog click handler — event delegation, so it also covers cards
    // injected later by load-more.js pagination.
    function handleCatalogClick(e) {
        var el = e.target.closest('.clckcatalog');
        if (!el) return;
        var infoLink = el.querySelector('.info');
        if (!infoLink) return;
        var href = infoLink.getAttribute('href');
        if (!href) return;
        // Check if href is already an absolute URL (from locale_route()).
        // If it starts with http, use it directly; otherwise prepend base URL.
        var url = (href.indexOf('http') === 0) ? href : BASE_URL + href;
        window.location = url;
    }

    // ===========================================================================
    // Native lazy-load enhancement — image error fallback + IO fallback
    // ===========================================================================
    function handleImageError(img) {
        if (!img.hasAttribute('data-error-handled')) {
            img.setAttribute('data-error-handled', 'true');
            img.src = IMG_MISSING;
            img.alt = 'Image not available';
        }
    }

    function addErrorHandling() {
        var allImages = document.querySelectorAll('img[loading="lazy"]');
        for (var i = 0; i < allImages.length; i++) {
            allImages[i].addEventListener('error', function () {
                handleImageError(this);
            });
        }
    }

    function setupNativeLazyLoadFallback() {
        // Path 1: modern browsers with native loading="lazy" support —
        // just add error handling, no IntersectionObserver needed.
        if ('loading' in HTMLImageElement.prototype) {
            addErrorHandling();
            return;
        }

        // Path 2: older browsers without native lazy-load — use
        // IntersectionObserver to swap placeholder → real src on
        // approach to viewport.
        var images = document.querySelectorAll('img[loading="lazy"]');
        if ('IntersectionObserver' in window) {
            var imageObserver = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (entry.isIntersecting) {
                        var img = entry.target;
                        if (img.dataset.lazySrc && img.src.indexOf('thumb_loading_image.webp') !== -1) {
                            img.src = img.dataset.lazySrc;
                            img.removeAttribute('data-lazy-src');
                        }
                        imageObserver.unobserve(img);
                    }
                });
            }, { rootMargin: '50px 0px' });

            images.forEach(function (img) {
                if (img.src && img.src.indexOf('thumb_loading_image.webp') === -1) {
                    img.dataset.lazySrc = img.src;
                    img.src = IMG_LOADING;
                }
                imageObserver.observe(img);
            });
        } else {
            // Path 3: very old browsers, no IO support either — load
            // all lazy images immediately.
            images.forEach(function (img) {
                if (img.dataset.lazySrc) {
                    img.src = img.dataset.lazySrc;
                    img.removeAttribute('data-lazy-src');
                }
            });
        }

        addErrorHandling();
    }

    // Re-attach error handling for dynamically-loaded images (e.g. via
    // load-more.js pagination on listing pages).
    function setupMutationObserver() {
        var observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.type !== 'childList') return;
                mutation.addedNodes.forEach(function (node) {
                    if (node.nodeType !== 1) return; // not an element
                    var newImages = node.querySelectorAll ? node.querySelectorAll('img[loading="lazy"]') : [];
                    newImages.forEach(function (img) {
                        img.addEventListener('error', function () {
                            handleImageError(this);
                        });
                    });
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    // IntersectionObserver deferral for below-fold data-defer-src images, keeping
    // their bytes out of the mobile pre-LCP window. Native loading="lazy" alone
    // under-defers — its threshold is too generous.
    function setupDeferredImages() {
        var imgs = document.querySelectorAll('img[data-defer-src]');
        if (!imgs.length) return;

        function reveal(img) {
            var srcset = img.getAttribute('data-defer-srcset');
            var src = img.getAttribute('data-defer-src');
            if (srcset) img.setAttribute('srcset', srcset);
            if (src) img.src = src;
            img.removeAttribute('data-defer-src');
            img.removeAttribute('data-defer-srcset');
        }

        // No IntersectionObserver (very old browsers) → reveal all immediately.
        if (!('IntersectionObserver' in window)) {
            for (var i = 0; i < imgs.length; i++) { reveal(imgs[i]); }
            return;
        }

        var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                    reveal(entry.target);
                    io.unobserve(entry.target);
                }
            });
        }, { rootMargin: '250px 0px' });

        for (var j = 0; j < imgs.length; j++) { io.observe(imgs[j]); }
    }

    // ===========================================================================
    // Init — register all handlers, deferred to browser idle time
    // ===========================================================================
    function initSiteJs() {
        // Event delegation — register once at document level.
        document.addEventListener('click', handleCatalogClick);

        // Native lazy-load enhancement + image error fallback.
        setupNativeLazyLoadFallback();

        // Swap in deferred (data-defer-src) images on scroll.
        setupDeferredImages();

        // MutationObserver for dynamically-loaded images.
        setupMutationObserver();
    }

    // ===========================================================================
    // Navigation + accordion interactions (moved from inline <script> blocks in
    // navigation.blade / master.blade — Phase 61.3). These bind IMMEDIATELY, not in
    // the idle-deferred init below, so they respond from first paint. All are
    // document-delegated, so binding as this deferred module runs (post-parse,
    // pre-DOMContentLoaded) is safe. The locale prefix comes from
    // <body data-locale-prefix> (set in master.blade), not URL sniffing.
    // ===========================================================================
    function localePrefix() {
        return (document.body && document.body.dataset.localePrefix) || '/en';
    }

    function initNavInteractions() {
        // City selector -> /{locale}/city/<slug>. Delegated 'change' (it bubbles) so it also
        // covers any selector rendered into the page later.
        document.addEventListener('change', function (e) {
            var sel = e.target.closest ? e.target.closest('.city-selector') : null;
            if (!sel || !sel.value) return;
            window.location.href = localePrefix() + '/city/' + sel.value;
        });

        // Language dropdown toggle — .dlz-open class (CSS-driven, no inline style).
        document.addEventListener('click', function (e) {
            if (e.target.closest('.locale-btn') || e.target.closest('.dlz-dropdown-toggle')) {
                e.preventDefault();
                var container = e.target.closest('.dlz-dropdown') || e.target.closest('.lang-item');
                var dropdownMenu = container ? container.querySelector('.dlz-dropdown-menu') : null;
                if (dropdownMenu) {
                    var isOpen = dropdownMenu.classList.contains('dlz-open');
                    document.querySelectorAll('.dlz-dropdown-menu.dlz-open').forEach(function (d) {
                        d.classList.remove('dlz-open');
                    });
                    if (!isOpen) {
                        dropdownMenu.classList.add('dlz-open');
                    }
                }
            } else if (e.target.closest('.dlz-dropdown-item')) {
                return; // let dropdown links work normally
            } else {
                document.querySelectorAll('.dlz-dropdown-menu.dlz-open').forEach(function (d) {
                    d.classList.remove('dlz-open');
                });
            }
        });

        // Search form submit -> clean path URL.
        document.addEventListener('submit', function (e) {
            var form = e.target;
            var searchInput = form.querySelector('input[name="q"]');
            if (!searchInput) return;
            e.preventDefault();
            var query = searchInput.value.trim();
            if (!query) return;
            var prefix = localePrefix();
            // Desktop form: a selected city redirects to the city filter page.
            if (form.id === 'catalogSearchForm') {
                var cityInput = form.querySelector('.city-input .dlz-form-control');
                if (cityInput && cityInput.value !== '') {
                    window.location.href = prefix + '/city/' + encodeURIComponent(cityInput.value);
                    return;
                }
            }
            window.location.href = prefix + '/search/' + encodeURIComponent(query);
        });

        // Search icon -> submit the desktop form.
        document.addEventListener('click', function (e) {
            if (e.target.closest('.dlz-input-group-text.bg-white')) {
                var form = document.getElementById('catalogSearchForm');
                if (form) {
                    if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); }
                }
            }
        });

        // Mobile search / map / cancel toggles.
        document.addEventListener('click', function (e) {
            if (e.target.closest('.search-toggle') || e.target.closest('.toggleFormBtn.search-toggle')) {
                var searchForm = document.querySelector('.form-container');
                if (searchForm) {
                    var currentDisplay = window.getComputedStyle(searchForm).display;
                    searchForm.style.display = currentDisplay === 'none' ? 'block' : 'none';
                }
            } else if (e.target.closest('.map-toggle') || e.target.closest('.toggleFormBtn.map-toggle')) {
                var mapForm = document.querySelector('.form-container');
                if (mapForm) {
                    mapForm.style.display = 'block';
                    setTimeout(function () {
                        var citySelector = mapForm.querySelector('.city-selector');
                        if (citySelector) {
                            citySelector.focus();
                            if (citySelector.size <= 1) {
                                citySelector.size = Math.min(citySelector.options.length, 4);
                                setTimeout(function () { citySelector.size = 1; }, 3000);
                            }
                        }
                    }, 100);
                }
            } else if (e.target.closest('.toggleFormBtn') && !e.target.closest('.search-toggle') && !e.target.closest('.map-toggle')) {
                var cancelForm = document.querySelector('.form-container');
                if (cancelForm) { cancelForm.style.display = 'none'; }
            }
        });
    }

    // Accordion / collapse (data-dlz-toggle="collapse"; no Bootstrap JS).
    // Covers: faq, catalog/show, menu/show, other_offer/show, store/show.
    function initCollapse() {
        document.addEventListener('click', function (e) {
            var btn = e.target.closest('[data-dlz-toggle="collapse"]');
            if (!btn) return;
            e.preventDefault();

            var targetSelector = btn.getAttribute('data-dlz-target');
            if (!targetSelector) return;
            var targetPanel = document.querySelector(targetSelector);
            if (!targetPanel) return;

            var parentSelector = targetPanel.getAttribute('data-dlz-parent');
            var isCurrentlyOpen = targetPanel.classList.contains('dlz-open');

            // Accordion: close sibling panels first.
            if (parentSelector) {
                var parentEl = document.querySelector(parentSelector);
                if (parentEl) {
                    parentEl.querySelectorAll('.dlz-collapse.dlz-open').forEach(function (openPanel) {
                        if (openPanel !== targetPanel) {
                            openPanel.classList.remove('dlz-open');
                            var siblingBtn = document.querySelector('[data-dlz-target="#' + openPanel.id + '"]');
                            if (siblingBtn) { siblingBtn.classList.add('dlz-collapsed'); siblingBtn.setAttribute('aria-expanded', 'false'); }
                        }
                    });
                }
            }

            if (isCurrentlyOpen) {
                targetPanel.classList.remove('dlz-open');
                btn.classList.add('dlz-collapsed');
                btn.setAttribute('aria-expanded', 'false');
            } else {
                targetPanel.classList.add('dlz-open');
                btn.classList.remove('dlz-collapsed');
                btn.setAttribute('aria-expanded', 'true');
            }
        });
    }

    // Interactive nav + accordion bind now (must work from first paint), not at idle.
    initNavInteractions();
    initCollapse();

    // Push execution to idle time (the `defer` attribute only makes the download
    // non-blocking). setTimeout fallback for Safari < 16.4.
    if ('requestIdleCallback' in window) {
        requestIdleCallback(initSiteJs, { timeout: 2000 });
    } else {
        setTimeout(initSiteJs, 100);
    }
})();
